import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";
import { LINKS } from "@/constants/links";

const HERO_IMG =
  "https://images.unsplash.com/photo-1681137063068-081072cf04b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBjYW1lcmElMjBmaWxtJTIwZGlyZWN0b3IlMjB2aWRlbyUyMHByb2R1Y3Rpb24lMjBzdHVkaW98ZW58MHx8fHwxNzg5NTA5MjA5fDA&ixlib=rb-4.1.0&q=85";

const CtaLink = ({ label, testId, target, primary = false, delay }) => (
  <motion.button
    data-testid={testId}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.8, ease: EASE }}
    onClick={() => {
      if (target === "#contact") return window.__openEnquiry();
      if (window.__scrollToId) window.__scrollToId(target);
      else document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }}
    className={`group flex items-center gap-3 font-display text-sm tracking-[0.08em] pb-2 border-b transition-colors duration-300 ${
      primary
        ? "text-[#F2F0EB] border-[#F2F0EB]"
        : "text-[#8A8A85] border-[#8A8A85]/40 hover:text-[#F2F0EB] hover:border-[#F2F0EB]"
    }`}
  >
    {label}
    <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
  </motion.button>
);

export const HeroSection = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY, opacity: bgOpacity }} className="absolute inset-0 -z-10">
        {LINKS.heroVideo && !videoFailed ? (
          <video
            ref={videoRef}
            data-testid="hero-bg-video"
            src={LINKS.heroVideo}
            poster={HERO_IMG}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={() => videoRef.current?.play().catch(() => {})}
            onError={() => setVideoFailed(true)}
            className="w-full h-full object-cover opacity-30 scale-110"
          />
        ) : (
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25 scale-110" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/40 to-[#0A0A0A]" />
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#8A8A85] mb-8 flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 pulse-dot" />
          Etia Media — Filming · Social Media · Ads, UK
        </motion.p>

        <h1 className="font-display leading-[0.95] text-[13vw] sm:text-[11vw] lg:text-[8.5vw]" data-testid="hero-heading">
          <MaskedLine delay={0.3}>Your Story.</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="text-outline">Filmed</span>
          </MaskedLine>
          <MaskedLine delay={0.6}>Differently.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#8A8A85] leading-relaxed"
          data-testid="hero-subtext"
        >
          I help your business create content that brings in more money —
          consistency builds trust, and trust brings paying clients. From shooting
          your big wedding day to building a real connection with your audience.
        </motion.p>

        <div className="mt-12 flex flex-wrap gap-x-10 gap-y-5">
          <CtaLink label="View My Work" testId="hero-view-work-btn" target="#work" primary delay={1} />
          <CtaLink label="Let's Work Together" testId="hero-work-together-btn" target="#contact" delay={1.1} />
          <CtaLink label="Or Book a 30-Min Call" testId="hero-book-call-btn" target="#contact" delay={1.2} />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 right-6 lg:right-10 flex items-center gap-3 text-[#8A8A85]"
      >
        <span className="font-mono2 text-[10px] uppercase tracking-[0.35em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
